import {
  ExtendedShareholderDTO,
  ShareholderDTO,
  totalNumberOfSharesOfShareholder,
  totalNumberOfSharesOfShareholders,
} from "@/types/Shareholder";
import { compact, groupBy, identity, uniqBy } from "lodash";
import { PortfolioCompany } from "@/stores/PortfolioStore";
import { isBlank } from "@/utils/strings";
import { percentOf } from "@/utils/numbers";
import { getFullName } from "@/utils/Roles";
import {
  CompanySelectorModel,
  extractPortfolioBaseUrl,
  SelectableCompany,
  SelectablePortfolio,
  portfolioApi,
  SelectedPortfolioOption,
} from "@/portfolio/portfolioDomain";
import { NamedOrganisationNumber } from "@/types/Transactions";
import { Language, languageLabels, supportedBackendLanguages } from "@/i18n/languages";
import { ApiService } from "@/api";
import { UploadShareholdersExcelResponse } from "@/api/ApiService";
import { makeAutoObservable } from "mobx";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { AdminItem, CompanyItem, failure, ok, pending, Result } from "@/common/common";
import { SendInvitationDialogStore } from "@/stores/SendInvitationDialogStore";
import { History } from "history";
import {
  BooleanFieldModel,
  createEmailField,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { ShareholderStatus } from "@/shareholderRegister/shareholderRegisterDomain";
import { filterShareholdersByStatus } from "@/stores/ShareholdersStore";
import { InvitationType } from "@/types/Invitation";
import { AIClient } from "@/ai/AIAssistant";
import { AxiosInstance } from "axios";
import { User } from "@/types/UserInfo";
import { DCEvent, EventDispatcher, showGeneralError } from "@/events/eventsDomain";
import { CompanyRole, CompanyroleId } from "@/types/CompanyInfo";

import { TMessage, TMessageParam } from "@/i18n/tt";
import { AggregatedDetailsFieldType, PersonInfoField } from "./aggregatedContactDetailsDomain";
import { ShareType } from "@/common/share";
import { FileUploadConfig } from "@/fileUpload/fileUploadDomain";
import { RoleForm } from "@/companyRoles/companyRolesDialogsDomain.ts";
import type { CompanyRoleFilters } from "./companyRolesDomain";

export type InvestmentStatus = "current" | "former" | "all";

export type RoleItem = {
  id: CompanyroleId;
  role: CompanyRole;
  company: CompanyItem;
};

export type OwnershipItem = {
  id: string;
  shareholder: ShareholderDTO;
  company: CompanyItem;
};

export type AggregatedContactItem = {
  aggregatedContactId: string;
  name: string;
  roleItems: RoleItem[];
  ownershipItems: OwnershipItem[];
};

export type AggregatedShareSummary = {
  sharetypeId: number;
  orgNo: string;
  numberOfShares: number;
  numberOfVotes: number;
};

export type GetContactsResponse = {
  contacts: AggregatedContactItem[];
  shareSummaries: AggregatedShareSummary[];
  shareTypes: ShareType[];
  importExcelConfig: FileUploadConfig;
  canEditShareholdersExcel: boolean;
};

export type AggregatedCompanyItem = {
  id: number;
  companyItem: CompanyItem;
  subText: TMessage | TMessageParam;
  itemType: "role" | "ownership";
};

export type EditDetailsFieldDialogState = {
  title: TMessage;
  isUpdating?: boolean;
  submitLabel: TMessage;
  aggregatedDetailsFieldType: AggregatedDetailsFieldType;
  isSubmitDisabled?: boolean;
  alertMessage?: TMessage;
  detailsFieldModel: PersonInfoField;
  selectedCompanyModel: OptionFieldModel<AggregatedCompanyItem>;
};

export type ContactsState = {
  response?: Result<GetContactsResponse>;
  roleNameFilter?: StringFieldModel;
  companyRoleFilters?: CompanyRoleFilters;
  form?: RoleForm;
  isSaving?: boolean;
  searchFieldModel?: StringFieldModel;
  detailsSearchFieldModel?: StringFieldModel;
  investmentStatus?: InvestmentStatus;
  investmentStatusModel?: RadioFieldModel<InvestmentStatus>;
  editDetailsFieldDialogState?: EditDetailsFieldDialogState;
  shareholderCompanyFilter?: RadioFieldModel<CompanyItem | null>;
  shareholderMissingEmailModel?: BooleanFieldModel;
  importExcelConfig?: FileUploadConfig;
  canEditShareholdersExcel?: boolean;
  shareholderExcelResult?: UploadShareholdersExcelResponse;
};

export type ContactRow = {
  id: string;
  name: string;
  representative?: string;
  companies: ContactCompany[];
  shareholderId: number;
  lastLogin: string | undefined;
};

export const updateContactsState = (
  f: (state?: ContactsState) => Partial<ContactsState>,
): DCEvent => ({
  type: "update-store",
  data: {
    contacts: (state) => ({
      ...state,
      ...f(state),
    }),
  },
});

export const resetDetailsSearch = (): DCEvent =>
  updateContactsState(() => ({ detailsSearchFieldModel: new StringFieldModel() }));

export type ContactsInitOptions = {
  missingEmail?: boolean;
  company?: string;
};

export const initContactsPage = (
  portfolio: SelectedPortfolioOption,
  options?: ContactsInitOptions,
): DCEvent[] => {
  const missingEmail = options?.missingEmail;
  return [
    updateContactsState(() => ({
      response: pending(),
    })),
    portfolioApi.getCompanyRoles({
      portfolio,
      onSuccess: (res) => [
        updateContactsState((state) => {
          const shareholderMissingEmailModel =
            state?.shareholderMissingEmailModel ?? new BooleanFieldModel({ value: false });
          if (missingEmail !== undefined) {
            shareholderMissingEmailModel.setValue(missingEmail);
          }
          const shareholderCompanyFilterValue =
            state?.shareholderCompanyFilter?.value?.organisationNumber ?? options?.company;
          return {
            response: ok(res.data),
            roleNameFilter: state?.roleNameFilter ?? new StringFieldModel(),
            searchFieldModel: state?.searchFieldModel ?? new StringFieldModel(),
            detailsSearchFieldModel: state?.detailsSearchFieldModel ?? new StringFieldModel(),
            investmentStatusModel: initInvestmentStatusFilter(state?.investmentStatus),
            shareholderCompanyFilter: selectedShareholderCompanyModel(
              res.data.contacts,
              shareholderCompanyFilterValue,
            ),
            shareholderMissingEmailModel,
            importExcelConfig: res.data.importExcelConfig,
            canEditShareholdersExcel: res.data.canEditShareholdersExcel,
          };
        }),
      ],
      onFailure: () => [
        showGeneralError(),
        updateContactsState(() => ({
          response: failure(),
        })),
      ],
    }),
  ];
};

const selectedShareholderCompanyModel = (
  contacts: AggregatedContactItem[],
  defaultValue?: string,
): RadioFieldModel<CompanyItem | null> => {
  const companyItems = uniqBy(
    contacts.flatMap((a) => a.ownershipItems.map((o) => o.company)),
    "organisationNumber",
  );
  const value = companyItems.find((item) => item.organisationNumber === defaultValue);
  return new RadioFieldModel<CompanyItem | null>({
    value: value ?? null,
    options: [null, ...companyItems],
    idFn: (c) => (c ? c.companyName : "all"),
    labelFn: (c) => (c ? { translated: c.companyName } : { key: "portfolio.all-companies" }),
  });
};

const initInvestmentStatusFilter = (
  value: InvestmentStatus | undefined,
): RadioFieldModel<InvestmentStatus> => {
  return new RadioFieldModel<InvestmentStatus>({
    value: value ?? "current",
    options: ["current", "former", "all"],
    labelFn: (option) => investmentLabel(option),
    idFn: identity,
    iconFn: (option) => {
      switch (option) {
        case "current":
          return { name: "circle", color: "success", weight: "fill", size: "xs" };
        case "former":
          return { name: "circle", color: "subtle", size: "xs" };
        case "all":
          return "notebook";
      }
    },
  });
};
const investmentLabel = (status: InvestmentStatus): TMessage => {
  switch (status) {
    case "all":
      return {
        key: "contacts.aggregate.investment-status.all",
      };
    case "current":
      return { key: "contacts.aggregate.investment-status.current" };
    case "former":
      return { key: "contacts.aggregate.investment-status.former" };
  }
};

export function prepareRowData(
  shareholders: ExtendedShareholderDTO[],
  companies: PortfolioCompany[],
): ContactRow[] {
  const shareholdersByOrgno = groupBy(shareholders, (s) => s.organizationNumber);
  const totalSharesByOrgno: Record<string, number> = {};
  Object.keys(shareholdersByOrgno).forEach((orgno) => {
    totalSharesByOrgno[orgno] = totalNumberOfSharesOfShareholders(shareholdersByOrgno[orgno]);
  });

  const shareholdersByKey = groupBy(shareholders, (s) => makeId(s));

  const companiesByOrgno = groupBy(companies, (c) => c.organisationNumber);
  return Object.keys(shareholdersByKey).map((key) => {
    const shareholders = shareholdersByKey[key];
    return {
      id: key,
      name: shareholders[0].name,
      representative: shareholders[0].isCompany
        ? getFullName(shareholders[0].contactInfo)
        : undefined,
      companies: shareholders.map((shareholder) => ({
        orgNo: shareholder.organizationNumber,
        name:
          companiesByOrgno[shareholder.organizationNumber]?.[0]?.name ??
          shareholder.organizationNumber,
        ownershipPercentage: percentOf(
          totalNumberOfSharesOfShareholder(shareholder),
          totalSharesByOrgno[shareholder.organizationNumber],
        ),
        shareholderId: shareholder.id,
        email: shareholder.email,
      })),
      shareholderId: shareholders[0].id,
      lastLogin: shareholders[0].lastLogin,
    };
  });
}

function makeId(shareholder: ExtendedShareholderDTO): string {
  return shareholder.naturalId ?? String(shareholder.id);
}

export type ContactCompany = {
  orgNo: string;
  name: string;
  ownershipPercentage: number;
  shareholderId: number;
  email?: string;
};

export function filterContactRows(
  shareholders: ContactRow[],
  filter: string,
  company?: CompanyItem | null,
  missingEmail?: boolean,
): ContactRow[] {
  const filterString = filter.toLowerCase();
  return shareholders.filter(
    (shareholder) =>
      matchesNameFilter(shareholder, filterString) &&
      matchesCompanyFilter(shareholder, company) &&
      matchesMissingEmailFilter(shareholder, missingEmail),
  );
}

export function prepareAndFilterContactRows(
  allCompanies: PortfolioCompany[],
  shareholders: ExtendedShareholderDTO[],
  statusFilter: ShareholderStatus,
  nameFilter: string,
  companyFilter?: CompanyItem | null,
  missingEmail?: boolean,
): ContactRow[] {
  return filterContactRows(
    prepareRowData(filterShareholdersByStatus(shareholders, statusFilter), allCompanies),
    nameFilter,
    companyFilter,
    missingEmail,
  );
}

export function filterAdminRows(
  admins: AdminItem[],
  filter: string,
  company?: NamedOrganisationNumber | null,
): AdminItem[] {
  const filterInput = filter.toLowerCase().trim();

  return admins.filter(
    (admin) =>
      matchesAdminSearchCompanyNames(admin, filterInput) && matchesSelectedCompany(admin, company),
  );
}

function matchesAdminSearchCompanyNames(admin: AdminItem, filterInput: string) {
  return (
    isBlank(filterInput) ||
    admin.name.toLowerCase().includes(filterInput) ||
    admin.companies.some((c) => c.companyName.toLowerCase().includes(filterInput))
  );
}

function matchesNameFilter(shareholder: ContactRow, filterString: string) {
  return (
    isBlank(filterString) ||
    shareholder.name.toLowerCase().includes(filterString) ||
    shareholder.companies.some((company) => company.name.toLowerCase().includes(filterString)) ||
    shareholder.representative?.toLowerCase().includes(filterString)
  );
}

function matchesSelectedCompany(admin: AdminItem, filterCompany?: NamedOrganisationNumber | null) {
  if (!filterCompany) {
    return true;
  }
  return admin.companies.some((c) => c.organisationNumber === filterCompany.orgNo);
}

function matchesCompanyFilter(shareholder: ContactRow, filterCompany?: CompanyItem | null) {
  if (!filterCompany) {
    return true;
  }
  return shareholder.companies.some(
    (company) => company.orgNo === filterCompany.organisationNumber,
  );
}

function matchesMissingEmailFilter(shareholder: ContactRow, missingEmail?: boolean) {
  if (!missingEmail) {
    return true;
  }
  return shareholder.companies.filter((c) => isBlank(c.email)).length > 0;
}

export class InviteNewAdminDialogStore {
  readonly locale: Language;
  state: "input" | "loading" | "success" | "error" = "input";
  email: StringFieldModel;
  language: RadioFieldModel<Language>;
  onClose: () => void;
  private apiService: ApiService;
  messageStore: AlertMessageStore;
  companySelector: CompanySelectorModel;
  constructor({
    locale,
    onClose,
    companies,
    apiService,
    messageStore,
    portfolios,
    selectedPortfolioId,
  }: {
    locale: Language;
    onClose: () => void;
    companies: SelectableCompany[];
    apiService: ApiService;
    messageStore: AlertMessageStore;
    portfolios: SelectablePortfolio[];
    selectedPortfolioId?: string;
  }) {
    this.locale = locale;
    this.language = new RadioFieldModel<Language>({
      value: locale,
      options: supportedBackendLanguages,
      idFn: identity,
      labelFn: (l) => ({ key: languageLabels[l] }),
    });
    this.onClose = onClose;
    this.messageStore = messageStore;
    this.email = createEmailField({ value: "", required: true });
    this.apiService = apiService;
    this.companySelector = new CompanySelectorModel({ companies, portfolios, selectedPortfolioId });
    makeAutoObservable(this);
  }

  get isLoading(): boolean {
    return this.state === "loading";
  }

  get isSubmitDisabled(): boolean {
    return (
      this.email.isEmpty || this.companySelector.selection.isNoneSelected || !this.email.isValid
    );
  }

  *submitInviteNewAdminDialog() {
    if (!this.email.validate() || this.companySelector.selection.isNoneSelected) {
      return;
    }
    this.state = "loading";
    try {
      yield this.apiService.inviteAdministratorOrCustomeRole({
        email: this.email.inputValue,
        language: this.language.value,
        inviteTo: [...this.companySelector.selection.selectedIds],
      });
      this.state = "success";
      this.onClose();
      this.messageStore.showSuccess({ key: "invite-user.success-message", params: { count: 1 } });
    } catch (e) {
      this.state = "error";
      this.messageStore.showError({ key: "general-error" });
      this.onClose();
    }
  }
}

type ResendInvite = {
  type: "resendInvite";
  form: SendInvitationDialogStore;
};

type RevokeAccess = {
  type: "revokeAccess";
  form: RevokeAccessForm;
};

type AddAdditionalCompany = {
  type: "addAdditionalCompany";
  form: AddAdditionalCompanyForm;
};

export type AdminEditDialogState = ResendInvite | RevokeAccess | AddAdditionalCompany;

export type State = "input" | "loading" | "success" | "error";

export class AdminDialogStore {
  admin: AdminItem;
  onClose: () => void;
  editDialogState?: AdminEditDialogState;
  readonly apiService: ApiService;
  selectedPortfolioOption: SelectedPortfolioOption;
  messageStore: AlertMessageStore;
  history: History;
  state: State = "input";
  availableCompanies: CompanyItem[];
  http: AxiosInstance;
  user: User | null;

  constructor(
    admin: AdminItem,
    onClose: () => void,
    apiService: ApiService,
    selectedPortfolioOption: SelectedPortfolioOption,
    messageStore: AlertMessageStore,
    history: History,
    availableCompanies: CompanyItem[],
    http: AxiosInstance,
    user: User | null,
  ) {
    this.admin = admin;
    this.onClose = onClose;
    this.availableCompanies = availableCompanies;
    this.apiService = apiService;
    this.selectedPortfolioOption = selectedPortfolioOption;
    this.messageStore = messageStore;
    this.history = history;
    this.http = http;
    this.user = user;
    makeAutoObservable(this);
  }

  closeDialogState() {
    this.editDialogState = undefined;
  }

  get availableCompaniesForInvitation(): CompanyItem[] {
    return this.availableCompanies.filter((p) =>
      this.admin.companies.every((a) => p.organisationNumber !== a.organisationNumber),
    );
  }

  openResendInviteDialog(lang: Language, companyItem: CompanyItem, orgNo: string) {
    this.editDialogState = {
      type: "resendInvite",
      form: new SendInvitationDialogStore(
        {
          invitationType: InvitationType.USER,
          organisationNumber: companyItem.organisationNumber,
          lang,
          title: { key: "invite-user.title" },
          description: {
            key: "invite-user.infoMessage",
            params: { companyName: companyItem.companyName },
          },
        },
        this.apiService,
        this.messageStore,
        () => this.closeDialogState(),
        new AIClient(this.http, orgNo),
        {
          key: "invite-user.email.default-text-administrator",
          params: {
            senderName: getFullName(this.user),
            email: this.user?.email,
            companyName: companyItem.companyName,
          },
        },
      ),
    };
  }

  onSuccessUpdateEmail(updateEmail: string) {
    this.admin.email = updateEmail;
  }

  openRevokeAccessDialog(
    orgNo: string,
    companyName: string,
    onNavigateBackToPortfolio: () => void,
  ) {
    this.editDialogState = {
      type: "revokeAccess",
      form: new RevokeAccessForm({
        apiService: this.apiService,
        name: this.admin.name,
        closeRevokeAccessDialog: () => this.closeDialogState(),
        messageStore: this.messageStore,
        orgNo,
        userId: this.admin.id,
        companyName,
        onSuccess: (company) => {
          this.admin.companies = this.admin.companies.filter(
            (c) => c.organisationNumber !== company.organisationNumber,
          );
        },
        onNavigateBackToPortfolio: () =>
          this.admin.companies.length === 0 && onNavigateBackToPortfolio(),
        state: this.state,
      }),
    };
  }

  openAddAdditionalCompanyDialog(language: Language) {
    this.editDialogState = {
      type: "addAdditionalCompany",
      form: new AddAdditionalCompanyForm({
        admin: this.admin,
        locale: language,
        onClose: () => this.closeDialogState(),
        apiService: this.apiService,
        messageStore: this.messageStore,
        selectedPortfolioOption: this.selectedPortfolioOption,
        state: this.state,
        availableCompanies: this.availableCompaniesForInvitation,
      }),
    };
  }
}

export class EditEmailForm {
  email: StringFieldModel;
  private apiService: ApiService;
  dispatch: EventDispatcher;
  externalId: string;
  onSuccess: (email: string) => void;
  closeEditDialog: () => void;
  adminName: string;
  state: State = "input";
  user: User | null = null;
  customRoleId?: string;
  refreshUserInformation: () => void;

  constructor({
    apiService,
    dispatch,
    externalId,
    email,
    onSuccess,
    closeEditDialog,
    adminName,
    user,
    customRoleId,
    refreshUserInformation,
  }: {
    apiService: ApiService;
    dispatch: EventDispatcher;
    externalId: string;
    email: string;
    onSuccess: (email: string) => void;
    closeEditDialog: () => void;
    adminName: string;
    user: User | null;
    customRoleId?: string;
    refreshUserInformation: () => void;
  }) {
    this.email = createEmailField({ value: email, required: true });
    this.apiService = apiService;
    this.dispatch = dispatch;
    this.externalId = externalId;
    this.onSuccess = onSuccess;
    this.closeEditDialog = closeEditDialog;
    this.adminName = adminName;
    this.user = user;
    this.customRoleId = customRoleId;
    this.refreshUserInformation = refreshUserInformation;
    makeAutoObservable(this);
  }

  get isSubmitDisabled(): boolean {
    return !this.email.isValid;
  }

  get isLoading(): boolean {
    return this.state === "loading";
  }

  *updateAdministratorEmail() {
    if (!this.email.validate()) {
      return;
    }
    this.state = "loading";
    try {
      yield this.apiService.updateAdministratorEmail({
        externalId: this.externalId,
        email: this.email.validatedValue,
        customRoleId: this.customRoleId,
      });

      // refresh email verification status if the user is updating their own email
      if (this.externalId === this.user?.externalId) {
        this.refreshUserInformation();
      }

      this.state = "success";
      this.dispatch([
        {
          type: "show-alert",
          messageType: "success",
          message: { key: "general-success" },
          messageParams: { autoHide: true },
        },
      ]);
      this.onSuccess(this.email.validatedValue);
      this.closeEditDialog();
    } catch (e) {
      this.state = "error";
      this.dispatch([
        {
          type: "show-alert",
          messageType: "error",
          message: { key: "general-error" },
          messageParams: { autoHide: true },
        },
      ]);
      this.closeEditDialog();
    }
  }
}

export class RevokeAccessForm {
  private apiService: ApiService;
  name: string;
  closeRevokeAccessDialog: () => void;
  messageStore: AlertMessageStore;
  orgNo: string;
  userId: string;
  companyName: string;
  onSuccess: (company: CompanyItem) => void;
  onNavigateBackToPortfolio: () => void;
  state: State;
  constructor({
    apiService,
    name,
    closeRevokeAccessDialog,
    messageStore,
    orgNo,
    userId,
    companyName,
    onSuccess,
    onNavigateBackToPortfolio,
    state,
  }: {
    apiService: ApiService;
    name: string;
    closeRevokeAccessDialog: () => void;
    messageStore: AlertMessageStore;
    orgNo: string;
    userId: string;
    companyName: string;
    onSuccess: (company: CompanyItem) => void;
    onNavigateBackToPortfolio: () => void;
    state: State;
  }) {
    this.apiService = apiService;
    this.name = name;
    this.closeRevokeAccessDialog = closeRevokeAccessDialog;
    this.messageStore = messageStore;
    this.orgNo = orgNo;
    this.userId = userId;
    this.companyName = companyName;
    this.onSuccess = onSuccess;
    this.onNavigateBackToPortfolio = onNavigateBackToPortfolio;
    this.state = state;
    makeAutoObservable(this);
  }

  get isLoading(): boolean {
    return this.state === "loading";
  }

  *confirmRevokeAccessAdmin() {
    this.state = "loading";
    try {
      yield this.apiService.revokeAdminAccess(this.orgNo, this.userId);
      this.state = "success";
      this.onSuccess({ companyName: this.companyName, organisationNumber: this.orgNo });
      this.messageStore.showSuccess(
        { key: "contacts.admins.revoke-onSuccess", params: { name: this.name } },
        { autoHide: true },
      );
      this.closeRevokeAccessDialog();
      this.onNavigateBackToPortfolio();
    } catch (e) {
      this.state = "error";
      this.messageStore.showError({ key: "settings.error.revokeAdminAccess" }, { autoHide: true });
      this.closeRevokeAccessDialog();
    }
  }
}

export class RevokeAllForm {
  private apiService: ApiService;
  orgNos: string[];
  externalId: string;
  name: string;
  closeRevokeAccessDialog: () => void;
  messageStore: AlertMessageStore;
  onNavigateBackToPortfolio: () => void;
  state: State;
  onSuccess: () => void;
  constructor({
    apiService,
    orgNos,
    externalId,
    name,
    closeRevokeAccessDialog,
    messageStore,
    onNavigateBackToPortfolio,
    state,
    onSuccess,
  }: {
    apiService: ApiService;
    orgNos: string[];
    externalId: string;
    name: string;
    closeRevokeAccessDialog: () => void;
    messageStore: AlertMessageStore;
    onNavigateBackToPortfolio: () => void;
    state: State;
    onSuccess: () => void;
  }) {
    this.apiService = apiService;
    this.orgNos = orgNos;
    this.externalId = externalId;
    this.name = name;
    this.closeRevokeAccessDialog = closeRevokeAccessDialog;
    this.messageStore = messageStore;
    this.onNavigateBackToPortfolio = onNavigateBackToPortfolio;
    this.state = state;
    this.onSuccess = onSuccess;
    makeAutoObservable(this);
  }

  get isLoading(): boolean {
    return this.state === "loading";
  }

  *confirmRevokeAll() {
    this.state = "loading";
    try {
      yield this.apiService.revokeAdminAccessAll(this.externalId, this.orgNos);
      this.state = "success";
      this.messageStore.showSuccess(
        { key: "contacts.admins.revoke-onSuccess", params: { name: this.name } },
        { autoHide: true },
      );
      this.onSuccess();
      this.closeRevokeAccessDialog();
      this.onNavigateBackToPortfolio();
    } catch (e) {
      this.state = "error";
      this.messageStore.showError({ key: "settings.error.revokeAdminAccess" }, { autoHide: true });
      this.closeRevokeAccessDialog();
    }
  }
}

export class AddAdditionalCompanyForm {
  state: "input" | "loading" | "success" | "error" = "input";
  admin: AdminItem;
  language: RadioFieldModel<Language>;
  onClose: () => void;
  private apiService: ApiService;
  selectedCompanies: string[] = [];
  messageStore: AlertMessageStore;
  selectedPortfolioOption: SelectedPortfolioOption;
  availableCompanies: CompanyItem[];

  constructor({
    admin,
    locale,
    onClose,
    apiService,
    messageStore,
    selectedPortfolioOption,
    state,
    availableCompanies,
  }: {
    admin: AdminItem;
    locale: Language;
    onClose: () => void;
    apiService: ApiService;
    messageStore: AlertMessageStore;
    selectedPortfolioOption: SelectedPortfolioOption;
    state: State;
    availableCompanies: CompanyItem[];
  }) {
    this.admin = admin;
    this.selectedPortfolioOption = selectedPortfolioOption;
    this.language = new RadioFieldModel<Language>({
      value: locale,
      options: supportedBackendLanguages,
      idFn: identity,
      labelFn: (l) => ({ key: languageLabels[l] }),
    });
    this.onClose = onClose;
    this.apiService = apiService;
    this.messageStore = messageStore;
    this.state = state;
    this.availableCompanies = availableCompanies;
    makeAutoObservable(this);
  }

  get isSubmitDisabled(): boolean {
    return this.selectedCompanies.length === 0;
  }

  get isLoading(): boolean {
    return this.state === "loading";
  }

  get isAlreadyAdminInAllCompanies(): boolean {
    return this.availableCompanies.every((availableCompany) =>
      this.admin.companies.some(
        (c) => availableCompany.organisationNumber === c.organisationNumber,
      ),
    );
  }

  get companyOptions(): CompanyItem[] {
    return this.availableCompanies.filter((availableCompany) =>
      this.admin.companies.every(
        (c) => availableCompany.organisationNumber !== c.organisationNumber,
      ),
    );
  }

  isSelected(orgNo: string): boolean {
    return this.selectedCompanies.includes(orgNo);
  }

  toggleSelected(orgNo: string) {
    if (this.isSelected(orgNo)) {
      this.selectedCompanies = this.selectedCompanies.filter((c) => c !== orgNo);
    } else {
      this.selectedCompanies.push(orgNo);
    }
  }

  selectAllCompanies() {
    this.selectedCompanies = this.companyOptions.map((c) => c.organisationNumber);
  }

  deselectAllCompanies() {
    this.selectedCompanies = [];
  }

  *submitAddCompanyInAdminDialog() {
    this.state = "loading";
    try {
      yield this.apiService.inviteAdministratorOrCustomeRole({
        email: this.admin.email,
        language: this.language.value,
        inviteTo: this.selectedCompanies,
      });
      this.state = "success";
      this.onClose();
      this.messageStore.showSuccess({ key: "general-success" }, { autoHide: true });
    } catch (e) {
      this.state = "error";
      this.messageStore.showError({ key: "general-error" }, { autoHide: true });
      this.onClose();
    }
  }
}

export const extractAggregatedCompanyRoleDetailsUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
  companyroleId: string | number,
): string =>
  compact([extractPortfolioBaseUrl(selectedPortfolioOption), "contacts/board", companyroleId]).join(
    "/",
  );

export const extractAggregatedShareholderDetailsUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
  shareholderId: number,
): string =>
  compact([
    extractPortfolioBaseUrl(selectedPortfolioOption),
    "contacts/shareholders",
    shareholderId,
  ]).join("/");
